import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Newspaper, PlusSquare, FolderKanban, ImagePlus,
  Settings, LogOut, ExternalLink, LayoutTemplate, Megaphone,
  Users, ScrollText, BarChart3, Bot, Instagram, Activity, Share2, BrainCircuit,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ATID } from "@/lib/adminTestIds";

// key, path, label, icon, requires (list of permissions; any one grants access)
const NAV = [
  { key: "home", to: "/admin", label: "Inicio", icon: LayoutDashboard, end: true, requires: null },
  { key: "articles", to: "/admin/noticias", label: "Noticias", icon: Newspaper,
    requires: ["articles.create", "articles.edit_any", "articles.edit_own", "articles.approve", "articles.publish"] },
  { key: "new-article", to: "/admin/noticias/nueva", label: "Nueva noticia", icon: PlusSquare,
    requires: ["articles.create"] },
  { key: "categories", to: "/admin/categorias", label: "Categorías", icon: FolderKanban,
    requires: ["categories.manage"] },
  { key: "homepage", to: "/admin/portada", label: "Portada", icon: LayoutTemplate,
    requires: ["homepage.manage"] },
  { key: "advertising", to: "/admin/publicidad", label: "Publicidad", icon: Megaphone,
    requires: ["ads.manage", "ads.stats"] },
  { key: "media", to: "/admin/multimedia", label: "Multimedia", icon: ImagePlus,
    requires: ["media.upload", "media.manage"] },
  { key: "social-images", to: "/admin/editor-fotos", label: "Editor de fotos", icon: Instagram,
    requires: ["articles.create"] },
  { key: "social-networks", to: "/admin/redes-sociales", label: "Redes Sociales", icon: Share2,
    requires: ["settings.manage", "articles.publish", "articles.create", "automation.manage"] },
  { key: "stats", to: "/admin/estadisticas", label: "Estadísticas", icon: BarChart3,
    requires: ["stats.view", "ads.stats"] },
  { key: "automation", to: "/admin/automatizacion", label: "Automatización", icon: Bot,
    requires: ["automation.view", "automation.manage"] },
  { key: "editorial-engine", to: "/admin/motor-editorial", label: "Motor Editorial", icon: BrainCircuit,
    requires: ["automation.manage"] },
  { key: "users", to: "/admin/usuarios", label: "Usuarios", icon: Users,
    requires: ["users.manage", "users.manage_all"] },
  { key: "audit", to: "/admin/auditoria", label: "Auditoría", icon: ScrollText,
    requires: ["audit.view"] },
  { key: "health", to: "/admin/estado-sistema", label: "Estado del sistema", icon: Activity,
    requires: ["settings.manage", "users.manage_all", "users.manage", "automation.manage", "automation.view"] },
  { key: "settings", to: "/admin/configuracion", label: "Configuración", icon: Settings,
    requires: ["settings.manage"] },
];

const ROLE_LABELS = {
  admin: "Administrador",
  director: "Director",
  editor: "Editor",
  periodista: "Periodista",
  colaborador: "Colaborador",
  comercial: "Comercial",
};

export default function AdminLayout({ children }) {
  const { user, logout, hasAnyPerm } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const visibleNav = NAV.filter((item) => {
    if (!item.requires) return true;
    return hasAnyPerm(...item.requires);
  });

  return (
    <div className="min-h-screen bg-lightgray text-blueblack flex">
      <aside data-testid={ATID.sidebar} className="hidden md:flex flex-col w-64 bg-nightblue text-white border-r border-softblue">
        <Link to="/admin" className="p-5 border-b border-white/10">
          <div className="font-serif text-2xl font-black leading-none">
            Sur<span className="text-watergreen">Primicia</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/60 mt-1.5">Panel</div>
        </Link>
        <nav className="flex-1 py-4 overflow-y-auto">
          {visibleNav.map(({ key, to, label, icon: Icon, end }) => (
            <NavLink
              key={key}
              to={to}
              end={!!end}
              data-testid={ATID.sidebarLink(key)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-[3px] transition-colors ${
                  isActive
                    ? "border-watergreen bg-white/5 text-watergreen"
                    : "border-transparent text-white/80 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-3">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/70 hover:text-watergreen transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Ver portal público
          </a>
          <div className="text-xs text-white/80 truncate leading-tight">
            <div className="text-white/60 uppercase tracking-widest text-[10px]">
              {ROLE_LABELS[user?.role] || user?.role}
            </div>
            <div className="truncate">{user?.name || user?.email}</div>
          </div>
          <button
            type="button"
            data-testid={ATID.sidebarLogout}
            onClick={handleLogout}
            className="w-full inline-flex items-center gap-2 justify-center bg-white/5 hover:bg-watergreen hover:text-nightblue text-white h-9 px-3 text-xs font-semibold uppercase tracking-widest transition-colors border border-white/10"
          >
            <LogOut className="w-3.5 h-3.5" /> Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile top bar */}
        <div className="md:hidden bg-nightblue text-white flex items-center justify-between px-4 py-3 border-b border-white/10">
          <Link to="/admin" className="font-serif text-lg font-black">
            Sur<span className="text-watergreen">Primicia</span>
          </Link>
          <button
            onClick={handleLogout}
            className="text-xs uppercase tracking-widest text-white/80 hover:text-watergreen"
          >
            Cerrar sesión
          </button>
        </div>
        {/* Mobile nav strip */}
        <div className="md:hidden bg-white border-b border-softblue overflow-x-auto">
          <div className="flex gap-1 px-2 py-2">
            {visibleNav.map(({ key, to, label, end }) => (
              <NavLink
                key={key}
                to={to}
                end={!!end}
                data-testid={ATID.sidebarLink(`${key}-m`)}
                className={({ isActive }) =>
                  `whitespace-nowrap px-3 py-1.5 text-xs font-semibold uppercase tracking-widest border ${
                    isActive
                      ? "bg-nightblue text-white border-nightblue"
                      : "border-softblue text-nightblue hover:bg-lightgray"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </div>
        </div>

        <main className="flex-1 p-5 sm:p-8 lg:p-10 max-w-full">{children}</main>
      </div>
    </div>
  );
}
